/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { BenchmarkScoreCard } from '@/app/(client)/audits/-components/digitized/BenchmarkScoreCard';
import { CustomScoringCard } from '@/app/(client)/audits/-components/digitized/CustomScoringCard';
import { ManagementSystemsTable } from '@/app/(client)/audits/-components/digitized/ManagementSystemsTable';
import { OverallRating } from '@/app/(client)/audits/-components/digitized/OverallRating';
import { PerformanceAreaTable } from '@/app/(client)/audits/-components/digitized/PerformanceAreaTable';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { useAuditDigitization } from '@/app/(client)/audits/-hooks/useAuditDigitization';
import {
  findManagementSystems,
  findPerformanceAreaRatings,
  isBsciAuditType,
  isSedexAuditType,
} from '@/app/(client)/audits/-utils/digitized';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/score/',
)({
  component: AuditScorePage,
});

function AuditScorePage() {
  const { auditId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'auditDetail' });
  const { data: audit } = useAuditDetail(auditId);
  const { data: digitized } = useAuditDigitization(auditId);

  const sections = digitized?.sections ?? [];
  const auditTypeId = audit?.auditTypeId;
  const performanceAreas = findPerformanceAreaRatings(sections);
  const managementSystems = findManagementSystems(sections);

  const hasBsci = isBsciAuditType(auditTypeId) && performanceAreas.length > 0;
  const hasSedex = isSedexAuditType(auditTypeId) && !!managementSystems;
  const hasScoreData = hasBsci || hasSedex;

  return (
    <div className='grid grid-cols-1 @5xl:grid-cols-5 @5xl:items-start gap-4 @5xl:gap-3 p-4 @md:p-6'>
      {/* Left: the overall rating — anchors the scoring from one side. */}
      <aside className='@5xl:col-start-1 @5xl:col-span-1 @5xl:sticky @5xl:top-16'>
        <OverallRating
          rating={digitized?.overall_rating ?? null}
          auditedBy={audit?.auditedBy}
          t={t}
        />
      </aside>

      {/* Center: the scoring & evaluation tables — three columns wide, the
          width of three top stat cards. */}
      <div className='@5xl:col-start-2 @5xl:col-span-3 min-w-0 flex flex-col gap-6'>
        {hasScoreData ? (
          <>
            {hasBsci ? (
              <PerformanceAreaTable rows={performanceAreas} t={t} />
            ) : null}
            {hasSedex && managementSystems ? (
              <ManagementSystemsTable data={managementSystems} t={t} />
            ) : null}
          </>
        ) : (
          <EmptyState
            icon={<Star className='size-5' />}
            title={t('score.empty.title')}
            description={t('score.empty.description')}
          />
        )}
      </div>

      {/* Right: benchmark + custom scoring as supporting sections — sticky
          beside the tables, balancing the overall rating on the left. */}
      <aside className='@5xl:col-start-5 @5xl:col-span-1 @5xl:sticky @5xl:top-16 flex flex-col gap-4'>
        <BenchmarkScoreCard t={t} />
        <CustomScoringCard t={t} />
      </aside>
    </div>
  );
}
